/**
 * MoveReasonDialog Component
 * Phase 6: Move Reason UI for manual ticket moves
 * Prompts user to select why they're moving a ticket to a different technician
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Brain, Wrench, User, Heart, Scale } from 'lucide-react';
import { ALL_MOVE_REASONS, type MoveReason } from '@/lib/moveReasonConstants';

interface MoveReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: MoveReason) => void;
  onCancel: () => void;
  fromTechName?: string;
  toTechName?: string;
  ticketLabel?: string;
}

const reasonIcons: Record<string, React.ReactNode> = {
  skill_match: <Wrench className="h-4 w-4" />,
  tech_preference: <User className="h-4 w-4" />,
  customer_request: <Heart className="h-4 w-4" />,
  specialty_match: <Brain className="h-4 w-4" />,
  capacity_balance: <Scale className="h-4 w-4" />,
  other: null,
};

export function MoveReasonDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  fromTechName,
  toTechName,
  ticketLabel,
}: MoveReasonDialogProps) {
  const [selectedReason, setSelectedReason] = useState<MoveReason>('skill_match');

  const handleConfirm = () => {
    onConfirm(selectedReason);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Why are you moving this ticket?
          </DialogTitle>
          <DialogDescription>
            {ticketLabel && (
              <span className="font-medium">{ticketLabel}</span>
            )}
            {fromTechName && toTechName && (
              <span className="block mt-1">
                Moving from <strong>{fromTechName}</strong> to <strong>{toTechName}</strong>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={selectedReason}
            onValueChange={(value) => setSelectedReason(value as MoveReason)}
            className="space-y-3"
          >
            {ALL_MOVE_REASONS.map((reason) => (
              <div
                key={reason.value}
                className="flex items-center space-x-3 rounded-lg border p-3 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => setSelectedReason(reason.value)}
              >
                <RadioGroupItem value={reason.value} id={reason.value} />
                <Label
                  htmlFor={reason.value}
                  className="flex-1 cursor-pointer flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    {reasonIcons[reason.value]}
                    <span>{reason.label}</span>
                  </div>
                  {reason.isLearning && (
                    <Badge variant="secondary" className="text-xs">
                      <Brain className="h-3 w-3 mr-1" />
                      Learning
                    </Badge>
                  )}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <p className="text-xs text-muted-foreground mt-4">
            <Brain className="h-3 w-3 inline mr-1" />
            Moves marked "Learning" help OptiBay learn your preferences over time
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Confirm Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
